import React, { useEffect, useRef, useState } from 'react';
import { X, ExternalLink } from 'lucide-react';
import type { PillarCardData, PillarCardRowData } from './MetricCard';

// In-page pillar dossier (owner audit 2026-09-26, SR-311..316). A full-height drawer over the Analysis page:
// the six pillars stay in place behind it, Escape / Back / the close button return to them, and the URL
// carries ?dossier=<pillar> so the view is linkable. "Open full dossier" goes to the standalone page.
// Section order: verdict → score-bearing inputs → context → decomposition → interpretation & limits;
// methodology and evidence sit behind their own tabs.

export interface PillarDossierData {
  construct: string;
  framework_ref: string;
  summary: string;
  weight_policy?: string;
  inputs: PillarCardRowData[];
  decomposition?: { label: string; weight: number; score: number | null; contribution: number | null; state: string; raw?: string; nominal_weight?: number }[];
  interpretation: string;
  limitations?: string[];
  run_points?: string[];
  evidence_states: { label: string; value: string; state?: string }[];
  provenance: { label: string; value: string }[];
  evidence?: { label: string; value: string }[];
  methodology: string;
  contract?: { label: string; value: string }[];
}

const tone = (state?: string): string => {
  switch (state) {
    case 'MEASURED': case 'CURATED_VERIFIED': case 'OBSERVED': case 'SEALED': case 'PRESENT':
      return 'border-emerald-700/60 bg-emerald-900/20 text-emerald-300';
    case 'WITHHELD': case 'NOT_APPLICABLE': case 'CONTEXT':
      return 'border-slate-600/70 bg-slate-800/40 text-slate-300';
    case 'FAILED': case 'BREACHED': case 'STALE_NOT_SCOREABLE':
      return 'border-red-800/60 bg-red-900/25 text-red-300';
    default:
      return 'border-amber-700/60 bg-amber-900/20 text-amber-300';
  }
};
const Chip: React.FC<{ state?: string; children?: React.ReactNode }> = ({ state, children }) =>
  state ? <span className={`inline-block rounded border px-1.5 py-[1px] text-[9.5px] font-mono whitespace-nowrap ${tone(state)}`}>{children ?? state.replace(/_/g, ' ')}</span> : null;

const H: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-cmd-textDim mb-2">{children}</h3>
);

const Rows: React.FC<{ rows: PillarCardRowData[] }> = ({ rows }) => (
  <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_auto] gap-x-4 gap-y-2 items-baseline text-[13px]">
    {rows.map((r, i) => (
      <React.Fragment key={i}>
        <div className="text-cmd-textMuted">{r.label}</div>
        <div className="text-right text-cmd-text">{r.value || '—'}</div>
        <div className="text-right">{r.state !== 'CONTEXT' ? <Chip state={r.state} /> : null}</div>
        {r.reason && <div className="col-span-3 -mt-1 text-[11px] text-cmd-textDim leading-snug">{r.reason}</div>}
      </React.Fragment>
    ))}
  </div>
);

const KV: React.FC<{ items: { label: string; value: string; state?: string }[] }> = ({ items }) => (
  <dl className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)_auto] gap-x-4 gap-y-1.5 text-[12.5px]">
    {items.map((it, i) => (
      <React.Fragment key={i}>
        <dt className="text-cmd-textMuted">{it.label}</dt>
        <dd className="text-cmd-text break-words">{it.value || '—'}</dd>
        <dd className="text-right">{it.state ? <Chip state={it.state} /> : null}</dd>
      </React.Fragment>
    ))}
  </dl>
);

type Tab = 'summary' | 'methodology' | 'evidence';

const PillarDossierDrawer: React.FC<{
  card: PillarCardData & { dossier: PillarDossierData };
  onClose: () => void;
  fullDossierHref?: string;
  snapshotSha?: string;
  lastUpdated?: string;
  contribution?: string | null;
}> = ({ card, onClose, fullDossierHref, snapshotSha, lastUpdated, contribution }) => {
  const d = card.dossier;
  const [tab, setTab] = useState<Tab>('summary');
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    panelRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const state = [card.evidence_state.replace(/_/g, ' '), ...(card.qualifiers ?? [])].join(' · ');

  return (
    <div className="fixed inset-0 z-[80] flex justify-end" role="dialog" aria-modal="true" aria-label={`${card.title} dossier`}>
      <button type="button" aria-label="Close dossier" onClick={onClose} className="absolute inset-0 bg-black/60 cursor-default" />
      <div ref={panelRef} tabIndex={-1} className="relative h-full w-full max-w-[920px] bg-cmd-bg border-l border-cmd-border overflow-y-auto focus:outline-none">
        {/* Verdict header */}
        <div className="sticky top-0 z-10 bg-cmd-bg/95 backdrop-blur border-b border-cmd-border px-6 pt-5 pb-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[12px] font-semibold uppercase tracking-[0.16em] text-cmd-textMuted">{card.title}</div>
              <div className="mt-0.5 text-[13px] text-cmd-text">{d.construct}</div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {fullDossierHref && (
                <a href={fullDossierHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.12em] text-cmd-textMuted hover:text-cmd-text">
                  Open full dossier <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              <button type="button" onClick={onClose} className="text-cmd-textDim hover:text-cmd-text" aria-label="Close dossier (Esc)"><X className="w-5 h-5" /></button>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-x-6 gap-y-2">
            <div className="font-mono tabular-nums text-cmd-text leading-none">
              <span className="text-[30px] font-semibold">{card.score_display}</span><span className="text-[13px] text-cmd-textDim"> /100</span>
            </div>
            <Chip state={card.evidence_state}>{state}</Chip>
            {card.coverage_label && <span className="text-[11.5px] font-mono text-cmd-textMuted">{card.coverage_label}</span>}
            {contribution && <span className="text-[11.5px] font-mono text-[color:var(--c-gold)]">{contribution}</span>}
          </div>
          {card.score_basis && <div className="mt-2 text-[12.5px] text-cmd-textMuted">{card.score_basis}</div>}
          <div className="mt-1 text-[11px] font-mono text-cmd-textDim">framework {d.framework_ref || '—'} · nominal weight {Math.round(card.nominal_weight)}%{card.load_bearing ? ' · load-bearing' : ''}</div>
          <div className="mt-3 flex gap-1" role="tablist">
            {(['summary', 'methodology', 'evidence'] as Tab[]).map((t) => (
              <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
                className={`px-3 py-1 text-[11px] uppercase tracking-[0.12em] border rounded-[2px] ${tab === t ? 'border-[color:rgb(var(--c-gold-rgb)/0.5)] text-[color:var(--c-gold)]' : 'border-transparent text-cmd-textDim hover:text-cmd-textMuted'}`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="px-6 py-5 space-y-7">
          {tab === 'summary' && (
            <>
              <section>
                <H>Current scoring basis</H>
                <p className="text-[13.5px] leading-relaxed text-cmd-textMuted max-w-[70ch]">{d.summary}</p>
                {d.weight_policy && <p className="mt-2 text-[12.5px] leading-relaxed text-amber-200/90 max-w-[70ch]">{d.weight_policy}</p>}
              </section>
              <section>
                <H>Score-bearing inputs</H>
                <Rows rows={d.inputs} />
              </section>
              {card.context && card.context.length > 0 && (
                <section>
                  <H>Context · not scored</H>
                  <Rows rows={card.context} />
                </section>
              )}
              {d.decomposition && d.decomposition.length > 0 && (
                <section>
                  <H>This run · decomposition</H>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12.5px] font-mono tabular-nums">
                      <thead><tr className="text-cmd-textDim text-left">
                        <th className="font-normal py-1">Component</th>{d.decomposition.some((c) => c.nominal_weight != null) && <th className="font-normal py-1 text-right">Nominal</th>}<th className="font-normal py-1 text-right">{d.decomposition.some((c) => c.nominal_weight != null) ? 'Effective' : 'Weight'}</th><th className="font-normal py-1 text-right">Raw</th><th className="font-normal py-1 text-right">Score</th><th className="font-normal py-1 text-right">Contribution</th><th />
                      </tr></thead>
                      <tbody>
                        {d.decomposition.map((c, i) => (
                          <tr key={i} className="border-t border-cmd-border">
                            <td className="py-1.5 font-sans text-cmd-text">{c.label}</td>
                            {d.decomposition!.some((x) => x.nominal_weight != null) && <td className="py-1.5 text-right text-cmd-textMuted">{c.nominal_weight != null ? `${Math.round(c.nominal_weight * 100)}%` : '—'}</td>}
                            <td className="py-1.5 text-right">{c.weight ? `${(c.weight * 100).toFixed(c.nominal_weight != null ? 1 : 0)}%` : '—'}</td>
                            <td className="py-1.5 text-right font-sans text-cmd-textMuted">{c.raw || '—'}</td>
                            <td className="py-1.5 text-right">{c.score != null ? c.score.toFixed(1) : '—'}</td>
                            <td className="py-1.5 text-right">{c.contribution != null ? c.contribution.toFixed(1) : '—'}</td>
                            <td className="py-1.5 text-right"><Chip state={c.state} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
              <section>
                <H>Interpretation</H>
                <p className="text-[13.5px] leading-relaxed text-cmd-text max-w-[70ch]">{d.interpretation}</p>
                {d.limitations && d.limitations.length > 0 && (
                  <ul className="mt-2 space-y-1 text-[12.5px] text-cmd-textMuted max-w-[70ch] list-disc pl-5">
                    {d.limitations.map((l, i) => <li key={i}>{l}</li>)}
                  </ul>
                )}
              </section>
              {d.run_points && d.run_points.length > 0 && (
                <section>
                  <H>This run</H>
                  <ul className="space-y-1 text-[12.5px] text-cmd-textMuted max-w-[70ch] list-disc pl-5">
                    {d.run_points.map((l, i) => <li key={i}>{l}</li>)}
                  </ul>
                </section>
              )}
            </>
          )}

          {tab === 'methodology' && (
            <>
              {d.contract && d.contract.length > 0 && (
                <section>
                  <H>Scoring contract</H>
                  <KV items={d.contract} />
                </section>
              )}
              <section>
                <H>Construct</H>
                <p className="text-[13.5px] leading-relaxed text-cmd-textMuted max-w-[70ch]">{d.summary}</p>
              </section>
              <section>
                <H>Method</H>
                <p className="text-[13px] leading-relaxed text-cmd-textMuted max-w-[70ch]">{d.methodology}</p>
                {d.weight_policy && <p className="mt-2 text-[12.5px] leading-relaxed text-cmd-textMuted max-w-[70ch]">{d.weight_policy}</p>}
              </section>
            </>
          )}

          {tab === 'evidence' && (
            <>
              <section>
                <H>Evidence state by metric family</H>
                <KV items={d.evidence_states} />
              </section>
              <section>
                <H>Provenance</H>
                <KV items={d.provenance} />
              </section>
              {d.evidence && d.evidence.length > 0 && (
                <section>
                  <H>Evidence record</H>
                  <KV items={d.evidence} />
                </section>
              )}
              {(snapshotSha || lastUpdated) && (
                <section className="text-[11px] font-mono text-cmd-textDim">
                  {lastUpdated && <div>data as of {lastUpdated}</div>}
                  {snapshotSha && <div title={snapshotSha}>snapshot {snapshotSha.replace(/^sha256:/, '').slice(0, 16)}</div>}
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PillarDossierDrawer;
